import type { Logger } from "@expert/logging";
import type { KyInstance } from "ky";

export interface AddressSuggestion {
    placeId: string;
    label: string;
}

export const fetchAddressSuggestions = async (
    client: KyInstance,
    logger: Logger,
    searchText: string,
    sessionToken: string,
): Promise<AddressSuggestion[]> => {
    const childLogger = logger.child({ module: "addressSuggestions" });
    if (searchText.length < 6) return [];
    try {
        const suggestions = await client
            .get("v2/address-autocomplete", { searchParams: { text: searchText, sessionToken } })
            .json<AddressSuggestion[]>();

        // The place id is the Autocomplete option value, and Mantine requires option values to be unique.
        return [...new Map(suggestions.map((suggestion) => [suggestion.placeId, suggestion])).values()];
    } catch (error: unknown) {
        childLogger.warn({ error }, "addressSuggestions failure");
        throw error;
    }
};

/**
 * Returns the picked place as `street, city, state, zip, country`, the format `addressSearchToAddress` splits.
 * Suggestion labels have no zip code, so the form can't use one until this resolves.
 */
export const fetchAddressDetails = async (
    client: KyInstance,
    logger: Logger,
    placeId: string,
    sessionToken: string,
): Promise<string> => {
    const childLogger = logger.child({ module: "addressDetails" });
    try {
        const { address } = await client
            .get("v2/address-autocomplete/details", { searchParams: { placeId, sessionToken } })
            .json<{ address: string }>();
        return address;
    } catch (error: unknown) {
        childLogger.warn({ error, placeId }, "addressDetails failure");
        throw error;
    }
};
